import {
  Box,
  Text,
  Group,
  Stack,
  Progress,
  ActionIcon,
  Image,
  useMantineTheme,
  rem,
} from '@mantine/core';
import { Dropzone, DropzoneProps, FileWithPath, IMAGE_MIME_TYPE, PDF_MIME_TYPE } from '@mantine/dropzone';
import { useState, forwardRef } from 'react';
import {
  IconUpload,
  IconPhoto,
  IconX,
  IconFile,
  IconFileTypePdf,
  IconFileTypeDoc,
  IconFileTypeXls,
  IconFileTypeZip,
  IconTrash,
  IconEye,
  IconCheck,
  IconAlertCircle,
} from '@tabler/icons-react';
import { Paper, Badge } from '@/components/ui';
import { usePreferencesStore } from '../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';

export interface UploadedFile {
  file: File;
  id: string;
  progress: number;
  status: 'pending' | 'uploading' | 'success' | 'error';
  error?: string;
  url?: string;
  preview?: string;
}

export interface FileUploadProps extends Omit<Partial<DropzoneProps>, 'onChange'> {
  value?: UploadedFile[];
  onChange?: (files: UploadedFile[]) => void;
  onUpload?: (file: File) => Promise<{ url: string } | void>;
  maxFiles?: number;
  maxSize?: number;
  accept?: string[];
  showPreview?: boolean;
  showProgress?: boolean;
  multiple?: boolean;
  disabled?: boolean;
  label?: string;
  description?: string;
  error?: string;
  gradientVariant?: 'primary' | 'secondary' | 'tertiary';
  variant?: 'dropzone' | 'button' | 'compact';
}

const getFileIcon = (mimeType: string) => {
  if (mimeType.startsWith('image/')) return IconPhoto;
  if (mimeType === 'application/pdf') return IconFileTypePdf;
  if (mimeType.includes('word') || mimeType.includes('document')) return IconFileTypeDoc;
  if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return IconFileTypeXls;
  if (mimeType.includes('zip') || mimeType.includes('compressed')) return IconFileTypeZip;
  return IconFile;
};

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const FileUpload = forwardRef<HTMLDivElement, FileUploadProps>(
  (
    {
      value = [],
      onChange,
      onUpload,
      maxFiles = 10,
      maxSize = 5 * 1024 * 1024, // 5MB
      accept,
      showPreview = true,
      showProgress = true,
      multiple = true,
      disabled = false,
      label,
      description,
      error,
      gradientVariant = 'primary',
      variant = 'dropzone',
      ...dropzoneProps
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const gradient = theme.other?.gradients?.[gradientVariant];
    const accentColor = typeof gradient?.from === 'string' ? gradient.from : undefined;
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const [uploading, setUploading] = useState(false);

    const handleDrop = async (files: FileWithPath[]) => {
      const remainingSlots = maxFiles - value.length;
      const filesToAdd = files.slice(0, remainingSlots);

      // Convert files to base64 for images
      const newFiles: UploadedFile[] = await Promise.all(
        filesToAdd.map(async (file) => {
          let preview: string | undefined = undefined;

          // Convert image to base64
          if (file.type.startsWith('image/')) {
            preview = await new Promise<string>((resolve) => {
              const reader = new FileReader();
              reader.onloadend = () => {
                resolve(reader.result as string);
              };
              reader.readAsDataURL(file);
            });
          }

          return {
            file,
            id: Math.random().toString(36).substr(2, 9),
            progress: 0,
            status: 'pending' as const,
            preview,
          };
        })
      );

      const updatedFiles = [...value, ...newFiles];
      onChange?.(updatedFiles);

      if (onUpload) {
        setUploading(true);

        for (const uploadFile of newFiles) {
          const fileIndex = updatedFiles.findIndex((f) => f.id === uploadFile.id);

          // Update to uploading status
          updatedFiles[fileIndex] = { ...updatedFiles[fileIndex], status: 'uploading', progress: 0 };
          onChange?.([...updatedFiles]);

          try {
            // Simulate progress
            const progressInterval = setInterval(() => {
              const currentFile = updatedFiles[fileIndex];
              if (currentFile.progress < 90) {
                updatedFiles[fileIndex] = {
                  ...currentFile,
                  progress: currentFile.progress + 10,
                };
                onChange?.([...updatedFiles]);
              }
            }, 100);

            const result = await onUpload(uploadFile.file);

            clearInterval(progressInterval);

            updatedFiles[fileIndex] = {
              ...updatedFiles[fileIndex],
              status: 'success',
              progress: 100,
              url: result?.url,
            };
            onChange?.([...updatedFiles]);
          } catch (err) {
            updatedFiles[fileIndex] = {
              ...updatedFiles[fileIndex],
              status: 'error',
              error: err instanceof Error ? err.message : 'Upload failed',
            };
            onChange?.([...updatedFiles]);
          }
        }

        setUploading(false);
      }
    };

    const handleRemove = (id: string) => {
      onChange?.(value.filter((f) => f.id !== id));
    };

    const canAddMore = value.length < maxFiles;

    return (
      <Box ref={ref}>
        {label && (
          <Text fw={500} size="sm" mb={4} style={accessibilityStyles}>
            {label}
          </Text>
        )}
        {description && (
          <Text size="xs" c="dimmed" mb="xs" style={accessibilityStyles}>
            {description}
          </Text>
        )}

        {canAddMore && (
          <Dropzone
            onDrop={handleDrop}
            maxSize={maxSize}
            accept={accept}
            multiple={multiple}
            disabled={disabled || uploading}
            loading={uploading}
            {...dropzoneProps}
          >
            <Group justify="center" gap="xl" mih={variant === 'compact' ? 60 : 120} style={{ pointerEvents: 'none' }}>
              <Dropzone.Accept>
                <IconUpload
                  style={{ width: rem(52), height: rem(52), color: accentColor }}
                  stroke={1.5}
                />
              </Dropzone.Accept>
              <Dropzone.Reject>
                <IconX
                  style={{ width: rem(52), height: rem(52), color: theme.colors.red[6] }}
                  stroke={1.5}
                />
              </Dropzone.Reject>
              <Dropzone.Idle>
                <IconPhoto
                  style={{ width: rem(52), height: rem(52), color: theme.colors.gray[6] }}
                  stroke={1.5}
                />
              </Dropzone.Idle>

              <Box>
                <Text size="xl" inline style={accessibilityStyles}>
                  Drag files here or click to select
                </Text>
                <Text size="sm" c="dimmed" inline mt={7} style={accessibilityStyles}>
                  {multiple
                    ? `Attach up to ${maxFiles} files, each file should not exceed ${formatFileSize(maxSize)}`
                    : `File should not exceed ${formatFileSize(maxSize)}`}
                </Text>
              </Box>
            </Group>
          </Dropzone>
        )}

        {error && (
          <Text size="xs" c="red" mt="xs" style={accessibilityStyles}>
            {error}
          </Text>
        )}

        {/* File list */}
        {value.length > 0 && (
          <Stack gap="xs" mt="md">
            {value.map((uploadedFile) => {
              const FileIcon = getFileIcon(uploadedFile.file.type);

              return (
                <Paper key={uploadedFile.id} variant="outlined" p="sm">
                  <Group justify="space-between" wrap="nowrap">
                    <Group wrap="nowrap" style={{ overflow: 'hidden', flex: 1 }}>
                      {showPreview && uploadedFile.preview ? (
                        <Image
                          src={uploadedFile.preview}
                          w={40}
                          h={40}
                          fit="cover"
                          radius="sm"
                        />
                      ) : (
                        <Box
                          w={40}
                          h={40}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: theme.colors.gray[1],
                            borderRadius: theme.radius.sm,
                          }}
                        >
                          <FileIcon size={20} />
                        </Box>
                      )}

                      <Box style={{ overflow: 'hidden', flex: 1 }}>
                        <Text size="sm" fw={500} truncate style={accessibilityStyles}>
                          {uploadedFile.file.name}
                        </Text>
                        <Text size="xs" c="dimmed" style={accessibilityStyles}>
                          {formatFileSize(uploadedFile.file.size)}
                        </Text>
                      </Box>
                    </Group>

                    <Group gap="xs" wrap="nowrap">
                      {uploadedFile.status === 'success' && (
                        <Badge color="green" variant="light" leftSection={<IconCheck size={12} />}>
                          Uploaded
                        </Badge>
                      )}
                      {uploadedFile.status === 'error' && (
                        <Badge color="red" variant="light" leftSection={<IconAlertCircle size={12} />}>
                          Failed
                        </Badge>
                      )}
                      {uploadedFile.status === 'uploading' && showProgress && (
                        <Box w={100}>
                            <Progress
                              value={uploadedFile.progress}
                              size="sm"
                              color={accentColor}
                              animated
                            />
                        </Box>
                      )}
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        onClick={() => handleRemove(uploadedFile.id)}
                        disabled={uploadedFile.status === 'uploading'}
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Group>
                  </Group>

                  {uploadedFile.error && (
                    <Text size="xs" c="red" mt="xs" style={accessibilityStyles}>
                      {uploadedFile.error}
                    </Text>
                  )}
                </Paper>
              );
            })}
          </Stack>
        )}
      </Box>
    );
  }
);

FileUpload.displayName = 'FileUpload';

export default FileUpload;
